/**
 * AdhocLoadsManagerDialog — manage existing adhoc loads and invoke the three
 * update-load flows added in Phase 2b:
 *   • Balaji-style: regenerate invoice with updated weight  (POST /{id}/generate-updated-invoice)
 *   • SVE-style:    record inline addition/deduction        (POST /{id}/adjustment)
 *   • SVE-style:    generate monthly summary invoice        (POST /generate-summary-invoice)
 *
 * Fully isolated from AddEditAdhocLoadDialog — that dialog is CREATE-only, this
 * one is READ + UPDATE + SUMMARY-GENERATE.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { RefreshCw, Plus, Minus, FileText, Truck, Check, Loader2, TrendingUp, Pencil } from 'lucide-react';
import { AddEditAdhocLoadDialog } from './AddEditAdhocLoadDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const b64ToBlob = (b64, type) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
};

const openPdf = (b64) => {
  const blob = b64ToBlob(b64, 'application/pdf');
  window.open(URL.createObjectURL(blob), '_blank');
};

export function AdhocLoadsManagerDialog({ open, onClose }) {
  const [loads, setLoads] = useState([]);
  const [masters, setMasters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null); // load_id currently doing an action
  const [openRow, setOpenRow] = useState(null); // load_id with an expanded action panel
  const [rowForm, setRowForm] = useState({}); // { total_kgs_updated | adjustment_type/adjustment_kgs }
  const [summaryForm, setSummaryForm] = useState({ adhoc_client_id: '', from_date: '', to_date: '' });
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [editLoad, setEditLoad] = useState(null); // when set, opens AddEditAdhocLoadDialog in edit mode

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [l, m] = await Promise.all([
        axios.get(`${API}/adhoc-loads`, { headers: authHeaders() }),
        axios.get(`${API}/adhoc-clients`, { headers: authHeaders() }),
      ]);
      setLoads(l.data || []);
      setMasters(m.data || []);
    } catch (e) {
      toast.error('Failed to load adhoc data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAll();
      setOpenRow(null);
      setRowForm({});
      setSummaryForm({ adhoc_client_id: '', from_date: '', to_date: '' });
    }
  }, [open]);

  const toggleRow = (load) => {
    if (openRow === load.load_id) {
      setOpenRow(null);
      setRowForm({});
    } else {
      setOpenRow(load.load_id);
      setRowForm(
        load.invoice_style === 'balaji'
          ? { total_kgs_updated: '' }
          : { adjustment_type: 'addition', adjustment_kgs: '', adjustment_amount: '' },
      );
    }
  };

  const runUpdatedInvoice = async (load) => {
    const kgs = parseFloat(rowForm.total_kgs_updated);
    if (!kgs || kgs <= 0) { toast.error('Enter valid updated kgs'); return; }
    setBusyId(load.load_id);
    try {
      const r = await axios.post(
        `${API}/adhoc-loads/${load.load_id}/generate-updated-invoice`,
        { total_kgs_updated: kgs },
        { headers: authHeaders() },
      );
      toast.success(`Updated invoice ${r.data.generated_invoice_number_2} generated`);
      if (r.data.pdf_base64) openPdf(r.data.pdf_base64);
      setOpenRow(null); setRowForm({});
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to regenerate invoice');
    } finally {
      setBusyId(null);
    }
  };

  const runAdjustment = async (load) => {
    const kgs = parseFloat(rowForm.adjustment_kgs);
    if (!kgs || kgs <= 0) { toast.error('Enter valid adjustment kgs'); return; }
    const payload = { adjustment_type: rowForm.adjustment_type, adjustment_kgs: kgs };
    if (rowForm.adjustment_amount) payload.adjustment_amount = parseFloat(rowForm.adjustment_amount);
    setBusyId(load.load_id);
    try {
      await axios.post(
        `${API}/adhoc-loads/${load.load_id}/adjustment`,
        payload, { headers: authHeaders() },
      );
      toast.success(`Adjustment recorded (${rowForm.adjustment_type} ${kgs}kg)`);
      setOpenRow(null); setRowForm({});
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record adjustment');
    } finally {
      setBusyId(null);
    }
  };

  const runSummaryInvoice = async () => {
    if (!summaryForm.adhoc_client_id) { toast.error('Pick a client'); return; }
    if (!summaryForm.from_date || !summaryForm.to_date) {
      toast.error('Pick from + to dates'); return;
    }
    setSummaryBusy(true);
    try {
      const r = await axios.post(
        `${API}/adhoc-loads/generate-summary-invoice`,
        summaryForm, { headers: authHeaders() },
      );
      toast.success(`Summary invoice ${r.data.generated_invoice_number} generated`);
      if (r.data.pdf_base64) openPdf(r.data.pdf_base64);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate summary invoice');
    } finally {
      setSummaryBusy(false);
    }
  };

  const sveMasters = masters.filter((m) => m.invoice_style === 'sve');

  // Group loads by client for cleaner display
  const groupedLoads = loads.reduce((acc, l) => {
    (acc[l.client_name] ||= []).push(l);
    return acc;
  }, {});

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto"
                     data-testid="adhoc-loads-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5" /> Manage Adhoc Loads
          </DialogTitle>
        </DialogHeader>

        {/* Loads list */}
        <div className="space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && loads.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-6">
              No adhoc loads yet. Create one via <b>+ → Adhoc Client</b> in the client dropdown.
            </div>
          )}
          {Object.entries(groupedLoads).map(([clientName, clientLoads]) => (
            <div key={clientName} className="space-y-1.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 px-1">
                {clientName}
                <Badge className={`ml-2 text-[10px] uppercase ${
                  clientLoads[0]?.invoice_style === 'balaji'
                    ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                }`}>{clientLoads[0]?.invoice_style}-style</Badge>
              </div>
              {clientLoads.map((load) => (
                <Card key={load.load_id} className="p-3 hover:bg-slate-50 transition"
                      data-testid={`adhoc-load-row-${load.load_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex-1 min-w-0 text-sm">
                      <div className="font-medium truncate">
                        {load.generated_invoice_number}
                        {load.invoice_count === 2 && (
                          <span className="ml-1 text-emerald-600 text-xs">
                            → {load.generated_invoice_number_2} (updated)
                          </span>
                        )}
                        {load.sve_load_type === 'Addition/Deduction' && (
                          <Badge className="ml-2 text-[9px] bg-slate-100 text-slate-700">SUMMARY</Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {load.date_sent} · {load.kgs?.toLocaleString('en-IN')}kg
                        {load.total_kgs_updated && (
                          <span className="text-emerald-700 ml-1">→ {load.total_kgs_updated.toLocaleString('en-IN')}kg</span>
                        )}
                        {load.additional_kgs > 0 && <span className="text-emerald-600 ml-1">+{load.additional_kgs}kg</span>}
                        {load.deduction_kgs > 0 && <span className="text-red-600 ml-1">−{load.deduction_kgs}kg</span>}
                        {' · '}₹{(load.total_amount || 0).toLocaleString('en-IN')}
                      </div>
                      {/* Transport + Profit — surfaces here same as main
                          silica card. Rate includes transport, so
                          profit_per_kg = max(rate − transport_rate, 0). */}
                      {Number(load.transport_rate || 0) > 0
                        && Number(load.kgs || 0) > 0
                        && Number(load.rate || 0) > 0 && (
                        <div className="text-[11px] mt-1 flex gap-3 flex-wrap"
                             data-testid={`adhoc-load-transport-${load.load_id}`}>
                          <span className="text-slate-500">
                            Transport <span className="font-semibold text-slate-700">
                              ₹{Math.round(Number(load.transport_rate) * Number(load.kgs)).toLocaleString('en-IN')}
                            </span>
                            <span className="text-slate-400 ml-1">· ₹{load.transport_rate}/kg</span>
                          </span>
                          <span className="text-emerald-700 font-semibold">
                            Profit ₹{Math.round(
                              Math.max(Number(load.rate) - Number(load.transport_rate), 0) * Number(load.kgs)
                            ).toLocaleString('en-IN')}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-1">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => setEditLoad(load)}
                        disabled={busyId === load.load_id}
                        data-testid={`adhoc-load-edit-${load.load_id}`}
                        title="Edit adhoc load"
                      >
                        <Pencil className="w-3 h-3 mr-1" />Edit
                      </Button>
                      {load.sve_load_type !== 'Addition/Deduction' && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => toggleRow(load)}
                          disabled={busyId === load.load_id}
                          data-testid={`adhoc-load-action-${load.load_id}`}
                        >
                          {load.invoice_style === 'balaji'
                            ? <><RefreshCw className="w-3 h-3 mr-1" />Update Weight</>
                            : <><TrendingUp className="w-3 h-3 mr-1" />Adjust</>}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Expanded action panel */}
                  {openRow === load.load_id && (
                    <div className="mt-3 pt-3 border-t space-y-2">
                      {load.invoice_style === 'balaji' ? (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
                          <div className="sm:col-span-2">
                            <Label className="text-xs">New Total KGs *</Label>
                            <Input type="number" step="1" value={rowForm.total_kgs_updated || ''}
                                   onChange={(e) => setRowForm({ ...rowForm, total_kgs_updated: e.target.value })}
                                   placeholder={`e.g. ${(load.kgs || 0) + 200}`}
                                   data-testid={`adhoc-load-updated-kgs-${load.load_id}`} />
                          </div>
                          <Button size="sm" onClick={() => runUpdatedInvoice(load)}
                                  disabled={busyId === load.load_id}
                                  data-testid={`adhoc-load-regenerate-${load.load_id}`}>
                            {busyId === load.load_id
                              ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                              : <FileText className="w-3 h-3 mr-1" />}
                            Regenerate Invoice
                          </Button>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
                          <div>
                            <Label className="text-xs">Type *</Label>
                            <Select value={rowForm.adjustment_type}
                                    onValueChange={(v) => setRowForm({ ...rowForm, adjustment_type: v })}>
                              <SelectTrigger data-testid={`adhoc-load-adj-type-${load.load_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="addition">+ Addition</SelectItem>
                                <SelectItem value="deduction">− Deduction</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">KGs *</Label>
                            <Input type="number" step="1" value={rowForm.adjustment_kgs || ''}
                                   onChange={(e) => setRowForm({ ...rowForm, adjustment_kgs: e.target.value })}
                                   placeholder="200"
                                   data-testid={`adhoc-load-adj-kgs-${load.load_id}`} />
                          </div>
                          <div>
                            <Label className="text-xs">Amount (₹, auto)</Label>
                            <Input type="number" step="0.01" value={rowForm.adjustment_amount || ''}
                                   onChange={(e) => setRowForm({ ...rowForm, adjustment_amount: e.target.value })}
                                   placeholder={`= kgs × ₹${load.rate}`}
                                   data-testid={`adhoc-load-adj-amount-${load.load_id}`} />
                          </div>
                          <Button size="sm" onClick={() => runAdjustment(load)}
                                  disabled={busyId === load.load_id}
                                  data-testid={`adhoc-load-adj-save-${load.load_id}`}>
                            {busyId === load.load_id
                              ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                              : <Check className="w-3 h-3 mr-1" />}
                            Record
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          ))}
        </div>

        {/* Summary invoice generator */}
        {sveMasters.length > 0 && (
          <Card className="p-4 border-purple-200 bg-purple-50 space-y-3 mt-4"
                data-testid="adhoc-summary-panel">
            <div className="text-sm font-semibold text-purple-900">
              Generate Monthly Summary Invoice (SVE-style clients)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Client *</Label>
                <Select value={summaryForm.adhoc_client_id}
                        onValueChange={(v) => setSummaryForm({ ...summaryForm, adhoc_client_id: v })}>
                  <SelectTrigger data-testid="adhoc-summary-client">
                    <SelectValue placeholder="Pick SVE-style client" />
                  </SelectTrigger>
                  <SelectContent>
                    {sveMasters.map((m) =>
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>,
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">From Date *</Label>
                <Input type="date" value={summaryForm.from_date}
                       onChange={(e) => setSummaryForm({ ...summaryForm, from_date: e.target.value })}
                       data-testid="adhoc-summary-from" />
              </div>
              <div>
                <Label className="text-xs">To Date *</Label>
                <Input type="date" value={summaryForm.to_date}
                       onChange={(e) => setSummaryForm({ ...summaryForm, to_date: e.target.value })}
                       data-testid="adhoc-summary-to" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={runSummaryInvoice} disabled={summaryBusy}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="adhoc-summary-generate">
                {summaryBusy
                  ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  : <FileText className="w-4 h-4 mr-1" />}
                Generate Summary Invoice
              </Button>
            </div>
          </Card>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="adhoc-manager-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AddEditAdhocLoadDialog
      key={editLoad?.load_id || 'none'}
      open={!!editLoad}
      editingLoad={editLoad}
      onClose={() => setEditLoad(null)}
      onSaved={() => { setEditLoad(null); fetchAll(); }}
      onRefresh={(fresh) => { if (fresh) setEditLoad(fresh); fetchAll(); }}
    />
    </>
  );
}
